import { useQuery } from "@tanstack/react-query";
import { dashboardApi, FilterParams } from "../api/dashboardApi";

export function useFilters() {
  return useQuery({
    queryKey: ["filters"],
    queryFn: () => dashboardApi.getFilters(),
    staleTime: 5 * 60_000, // filter options rarely change
  });
}

export function useHeader(filters: FilterParams | null) {
  return useQuery({
    queryKey: ["header", filters],
    queryFn: () => dashboardApi.getHeader(filters!),
    enabled: !!filters,
  });
}

export function useKpis(filters: FilterParams | null) {
  return useQuery({
    queryKey: ["kpis", filters],
    queryFn: () => dashboardApi.getKpis(filters!),
    enabled: !!filters,
    placeholderData: (prev: any) => prev,
  });
}

export function useVarianceChart(filters: FilterParams | null) {
  return useQuery({
    queryKey: ["variance-chart", filters],
    queryFn: () => dashboardApi.getVarianceChart(filters!),
    enabled: !!filters,
  });
}

export function useTrendChart(filters: FilterParams | null) {
  return useQuery({
    queryKey: ["trend-chart", filters],
    queryFn: () => dashboardApi.getTrendChart(filters!),
    enabled: !!filters,
    placeholderData: (prev: any) => prev,
  });
}

export function useInsights(filters: FilterParams | null) {
  return useQuery({
    queryKey: ["insights", filters],
    queryFn: () => dashboardApi.getInsights(filters!),
    enabled: !!filters,
  });
}

export function useApprovals(filters: FilterParams | null) {
  return useQuery({
    queryKey: ["approvals", filters],
    queryFn: () => dashboardApi.getApprovals(filters!),
    enabled: !!filters,
  });
}

export function useVarianceExplorer(filters: FilterParams | null) {
  return useQuery({
    queryKey: ["variance-explorer", filters],
    queryFn: () => dashboardApi.getVarianceExplorer(filters!),
    enabled: !!filters,
  });
}

export function useFinancialIntelligence(filters: FilterParams | null) {
  return useQuery({
    queryKey: ["financial-intelligence", filters],
    queryFn: () => dashboardApi.getFinancialIntelligence(filters!),
    enabled: !!filters,
  });
}

export function useExecutiveSummary(filters: FilterParams | null) {
  return useQuery({
    queryKey: ["executive-summary", filters],
    queryFn: () => dashboardApi.getExecutiveSummary(filters!),
    enabled: !!filters,
    placeholderData: (prev: any) => prev,
  });
}

export function useAudit() {
  return useQuery({
    queryKey: ["audit"],
    queryFn: () => dashboardApi.getAudit(),
    staleTime: 60_000,
  });
}

export function useVePostings(fiscalYear: string | undefined, accountName: string | undefined, costCenter?: string) {
  return useQuery({
    queryKey: ["ve-postings", fiscalYear, accountName, costCenter],
    queryFn: () => dashboardApi.getVePostings(fiscalYear!, accountName!, costCenter),
    enabled: !!fiscalYear && !!accountName && accountName !== "All",
  });
}

export function useVeCostCenters(fiscalYear: string | undefined, accountName: string | undefined, costCenter?: string) {
  return useQuery({
    queryKey: ["ve-cost-centers", fiscalYear, accountName, costCenter],
    queryFn: () => dashboardApi.getVeCostCenters(fiscalYear!, accountName!, costCenter),
    enabled: !!fiscalYear && !!accountName && accountName !== "All",
  });
}

export function useMonthlyTrends(filters: FilterParams | null) {
  return useQuery({
    queryKey: ["monthly-trends", filters],
    queryFn: () => dashboardApi.getMonthlyTrends(filters!),
    enabled: !!filters,
    placeholderData: (prev: any) => prev,
  });
}
